import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AlertOctagon } from "lucide-react";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { routePath } from "~/shared/constants";

interface ErrorPageProps {
  title: string;
  message: string;
  backLink?: string;
  onRetry?: () => void;
  error?: Error | string;
}

export const ErrorPage = ({
  title,
  message,
  backLink = "/",
  error,
  onRetry,
}: ErrorPageProps) => {
  const { t } = useTranslation(["error"]);

  return (
    <div className="flex flex-col space-y-10 h-[75vh] flex-1 items-center justify-center text-center max-w-xl mx-auto container">
      <div className="rounded-full bg-icon-error-ring p-3">
        <div className="rounded-full bg-icon-error-background p-2">
          <AlertOctagon className="text-system-error h-8 w-8" />
        </div>
      </div>

      <div className="space-y-2">
        <Text as="h1" variant="pageHeading">
          {title}
        </Text>
        <Text variant="subtle" className="whitespace-pre">
          {message}
        </Text>
      </div>

      {error && (
        <Text variant="error">
          {error instanceof Error ? error.message : error}
        </Text>
      )}

      <div className="grid grid-flow-col gap-3">
        {/* A plain anchor: the app-level error screens render outside the router. */}
        <a
          href={routePath("/contact-us")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="support-action"
        >
          <Button
            label={t("error:actions.support")}
            variant="tertiary"
            size="small"
            className="text-sm"
          />
        </a>

        {onRetry ? (
          <Button
            label={t("error:actions.reload")}
            variant="primary"
            size="small"
            onClick={onRetry}
            className="text-sm"
          />
        ) : (
          <Link to={backLink}>
            <Button
              label={t("error:actions.back")}
              variant="primary"
              size="small"
              className="text-sm font-normal"
            />
          </Link>
        )}
      </div>
    </div>
  );
};
